import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SequenceAvailabilityType, SequenceStatusEnum, SequenceType } from "../../../types/Questionnaire";
import { AnswerValueType } from "../../../types/Answer";
import { QuestionType, QuestionTypeEnum } from "../../../types/Question";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfig } from "../../../contexts/ConfigContext";
import {
  getApiSequenceAvailability,
  getApiSequences,
  getApiFullSequence,
  FullSequenceResponse,
} from "../../../services/questionnaire.service";
import { getUserScores } from "../../../services/sequence.service";
import { getProjectConfig } from "../../../projects";

interface Score {
  id: number;
  userSequenceId: number;
  sequenceId: number;
  sequenceComponentId: number;
  questionnaireId: number;
  score: number;
  doneAt: string;
  recurrenceType: "daily" | "weekly" | "fortnightly" | "monthly" | "yearly" | null;
}

interface ResonanceDataContextValue {
  sequences: SequenceType[];
  scores: Score[];
  sequenceAvailabilityBySequenceId: Record<number, SequenceAvailabilityType>;
  hasFetchedSequences: boolean;
  loading: boolean;
  error: Error | null;

  fetchSequences: () => Promise<SequenceType[] | undefined>;
  fetchScores: () => Promise<void>;
  fetchSequenceAvailability: () => Promise<void>;

  /**
   * Staleness-gated background refresh, meant to be called on island entry. Refetches
   * (stale-while-revalidate — never blanks the screen) only when the in-memory snapshot
   * is provably stale; a healthy same-day snapshot is left untouched. Pass { force: true }
   * when the caller has detected a concrete problem (an island whose sequence is missing),
   * and { bypassThrottle: true } on top of it for a USER-initiated retry.
   *
   * The returned promise resolves once the refresh it triggered (or joined) has landed, so
   * a caller can tell "still loading" apart from "refreshed, and the data is still missing".
   */
  ensureFreshSequences: (opts?: {
    force?: boolean;
    bypassThrottle?: boolean;
  }) => Promise<void>;

  fullSequenceByUserSequenceId: Record<number, FullSequenceResponse>;

  // ✅ UPDATED SIGNATURE
  prefetchFullSequencesForStudySequenceIds: (
    studySequenceIds: number[],
    seqs?: SequenceType[]
  ) => Promise<void>;

  updateScore: (
    id: number,
    userSequenceId: number,
    sequenceId: number,
    sequenceComponentId: number,
    questionnaireId: number,
    score: number
  ) => void;

  updateSequenceComponentDone: (userSequenceId: number, sequenceComponentId: number) => void;

  updateSequenceComponentProgress: (
    userSequenceId: number,
    sequenceComponentId: number,
    answeredCount: number
  ) => void;

  updateFullSequenceAnswer: (
    userSequenceId: number,
    questionnaireId: number,
    question: QuestionType,
    value: AnswerValueType
  ) => void;

  updateSequenceDone: (userSequenceId: number) => void;
}

const ResonanceDataContext = createContext<ResonanceDataContextValue>({
  sequences: [],
  scores: [],
  sequenceAvailabilityBySequenceId: {},
  hasFetchedSequences: false,
  loading: false,
  error: null,

  fetchSequences: async () => undefined,
  fetchScores: async () => {},
  fetchSequenceAvailability: async () => {},
  ensureFreshSequences: async () => {},

  fullSequenceByUserSequenceId: {},
  prefetchFullSequencesForStudySequenceIds: async () => {},

  updateScore: () => {},
  updateSequenceComponentDone: () => {},
  updateSequenceComponentProgress: () => {},
  updateFullSequenceAnswer: () => {},
  updateSequenceDone: () => {},
});

export const ResonanceDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { study } = useConfig();
  const {
    i18n: { language },
  } = useTranslation();

  const [sequences, setSequences] = useState<SequenceType[]>([]);
  const [scores, setScores] = useState<Score[]>([]);
  const [sequenceAvailabilityBySequenceId, setSequenceAvailabilityBySequenceId] =
    useState<Record<number, SequenceAvailabilityType>>({});
  const [hasFetchedSequences, setHasFetchedSequences] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [fullSequenceByUserSequenceId, setFullSequenceByUserSequenceId] =
    useState<Record<number, FullSequenceResponse>>({});

  // Staleness bookkeeping for the island-entry background refresh (see ensureFreshSequences).
  // Refs, not state: read imperatively from an Ionic lifecycle callback and must never
  // trigger a re-render themselves.
  const lastFetchedDayRef = useRef<string | null>(null);
  const lastFetchedAtRef = useRef<number>(0);
  const lastRefreshAttemptRef = useRef<number>(0);
  // The in-flight refresh itself, not just a boolean: a second caller AWAITS it instead of
  // resolving instantly, so "the refresh finished and the row is still missing" is an outcome
  // callers can actually observe (see IslandDetailSlide's missing-sequence escape hatch).
  const refreshInFlightRef = useRef<Promise<void> | null>(null);

  // Device-local calendar day (YYYY-M-D) — matches the app's existing local-day convention
  // (pages/Suivi/urgency.ts); used only to detect a day rollover since the last fetch.
  const localDayKey = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  };

  // Clear all per-user data when the user logs out (user -> null). Logout is a
  // soft in-app navigation (no page reload), so without this the next account —
  // e.g. a signup right after a logout — would inherit the previous user's
  // sequences and full-sequence cache. Reset only on logout (never mid-session),
  // so it can't race the authenticated re-fetch in ResonanceInit.
  useEffect(() => {
    if (!user) {
      setSequences([]);
      setScores([]);
      setSequenceAvailabilityBySequenceId({});
      setFullSequenceByUserSequenceId({});
      setHasFetchedSequences(false);
      setError(null);
      lastFetchedDayRef.current = null;
      lastFetchedAtRef.current = 0;
      lastRefreshAttemptRef.current = 0;
    }
  }, [user]);

  const fetchSequences = async (): Promise<SequenceType[] | undefined> => {
    if (!user || !study?.id) return;
    // SWR: only take over the screen with the full-page loader when there is nothing to
    // show yet. A background refresh over existing data (island-entry revalidation) stays
    // silent so the current island doesn't flash a spinner on every tap.
    if (!sequences.length) setLoading(true);
    setError(null);

    try {
      const response = await getApiSequences({
        studyId: study.id,
        userId: user.id,
        lang: language,
      });
      setSequences(response.data);
      // Stamp a successful fetch so ensureFreshSequences can detect a day rollover.
      lastFetchedDayRef.current = localDayKey();
      lastFetchedAtRef.current = Date.now();
      return response.data;
    } catch (err: any) {
      console.error("Failed to fetch sequences", err);
      setError(err);
      return;
    } finally {
      setHasFetchedSequences(true);
      setLoading(false);
    }
  };

  interface ScoresResponse {
    scores: Score[];
    success?: boolean;
  }

  const fetchScores = async () => {
    if (!user || !study?.id) return;
    try {
      const response = await getUserScores({
        studyId: study.id,
        userId: user.id,
      });

      const raw = response.data;

      let scoresArray: Score[] = [];
      if (Array.isArray(raw)) {
        scoresArray = raw as Score[];
      } else if (raw && Array.isArray((raw as ScoresResponse).scores)) {
        scoresArray = (raw as ScoresResponse).scores;
      }

      setScores(scoresArray);
    } catch (err: any) {
      console.error("Failed to fetch scores", err);
      setError(err);
      setScores([]);
    }
  };

  const fetchSequenceAvailability = async () => {
    if (!user || !study?.id) return;

    try {
      const response = await getApiSequenceAvailability({
        studyId: study.id,
        userId: user.id,
      });

      const availabilityArray = response.data?.availability ?? [];
      setSequenceAvailabilityBySequenceId(
        Object.fromEntries(
          availabilityArray.map((availability) => [availability.sequenceId, availability])
        )
      );
    } catch (err: any) {
      console.error("Failed to fetch sequence availability", err);
      setError(err);
      setSequenceAvailabilityBySequenceId({});
    }
  };

  // ✅ Prefetch FullSequence for inline study sequence ids
  const prefetchFullSequencesForStudySequenceIds = async (
    studySequenceIds: number[],
    seqs?: SequenceType[]
  ) => {
    const source = seqs ?? sequences;
    if (!user || !study?.id) return;
    if (!studySequenceIds?.length) return;

    console.log("Starting prefetch for studySequenceIds:", studySequenceIds);

    const targets = source
      .filter((s) => studySequenceIds.includes(s.sequence_id))
      .map((s) => s.id); // ✅ USER sequence id

    const uniqueTargets = Array.from(new Set(targets)).filter(
      (userSequenceId) => !fullSequenceByUserSequenceId[userSequenceId]
    );

    console.log("Prefetch target userSequenceIds:", uniqueTargets);

    if (!uniqueTargets.length) return;

    const results = await Promise.allSettled(
      uniqueTargets.map((userSequenceId) => {
        console.log("Prefetching FULL sequence for userSequenceId:", userSequenceId);
        return getApiFullSequence({
          studyId: study.id,
          userId: user.id,
          sequenceId: userSequenceId,
          lang: language,
        }).then((r) => ({ userSequenceId, data: r.data }));
      })
    );

    setFullSequenceByUserSequenceId((prev) => {
      const next = { ...prev };
      for (const r of results) {
        if (r.status === "fulfilled") {
          next[r.value.userSequenceId] = r.value.data;
        }
      }
      console.log("Cache updated. Keys now:", Object.keys(next));
      return next;
    });
  };

  // Staleness-gated background refresh, called on island entry. Refetches (SWR — no screen
  // blanking) ONLY when the in-memory snapshot is provably stale: the app day rolled over
  // since the last fetch (the "left the app open overnight" case), the list is empty, a
  // prior fetch errored, or the caller forces it (an island whose sequence is missing). A
  // healthy same-day snapshot is left untouched, so a normal island tap costs nothing.
  const ensureFreshSequences = async (opts?: {
    force?: boolean;
    bypassThrottle?: boolean;
  }): Promise<void> => {
    if (!user || !study?.id) return;
    // Join the running refresh instead of resolving straight away: a caller that awaits us
    // must not conclude "refreshed, still nothing" while the fetch is still in the air.
    if (refreshInFlightRef.current) return refreshInFlightRef.current;

    const dayChanged =
      lastFetchedDayRef.current !== null && lastFetchedDayRef.current !== localDayKey();
    const neverFetched = !hasFetchedSequences || lastFetchedDayRef.current === null;
    const stale =
      !!opts?.force || neverFetched || dayChanged || sequences.length === 0 || error !== null;
    if (!stale) return;

    // Throttle repeated attempts while a stale condition persists (a genuinely empty study,
    // or a backend regeneration gap that hasn't filled yet) so re-entering an island can't
    // hammer the API. A real day rollover fires once, then the successful fetch clears it.
    // A user-initiated retry bypasses it — a tap can't hammer anything, and a "Réessayer"
    // button that silently does nothing for 30s reads as broken.
    const THROTTLE_MS = 30_000;
    if (!opts?.bypassThrottle && Date.now() - lastRefreshAttemptRef.current < THROTTLE_MS) return;
    lastRefreshAttemptRef.current = Date.now();

    const run = (async () => {
      const seqs = await fetchSequences();
      // Scores + availability roll over on the same daily boundary — refresh them too.
      await Promise.all([fetchScores(), fetchSequenceAvailability()]);
      if (seqs?.length) {
        const islandSequences = getProjectConfig().islandSequenceIds ?? {};
        const studySequenceIds = Array.from(
          new Set(
            Object.values(islandSequences)
              .map((ids) => ids.inline)
              .filter(Boolean)
          )
        ) as number[];
        await prefetchFullSequencesForStudySequenceIds(studySequenceIds, seqs);
      }
    })();

    refreshInFlightRef.current = run;
    try {
      await run;
    } catch (err) {
      // Every fetch above records its own failure in `error`; swallow here so the callers'
      // fire-and-forget `void ensureFreshSequences()` can never raise an unhandled rejection.
      console.error("Failed to refresh sequences", err);
    } finally {
      refreshInFlightRef.current = null;
    }
  };

  // --- existing updateScore / updateSequence* code unchanged ---
  const updateScore = (
    id: number,
    userSequenceId: number,
    sequenceId: number,
    sequenceComponentId: number,
    questionnaireId: number,
    score: number
  ) => {
    setScores((prev) => {
      const existingIndex = prev.findIndex(
        (s) =>
          s.userSequenceId === userSequenceId &&
          s.sequenceComponentId === sequenceComponentId &&
          s.questionnaireId === questionnaireId
      );

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          id,
          score,
          doneAt: new Date().toISOString(),
        };
        return updated;
      }

      return [
        ...prev,
        {
          id,
          userSequenceId,
          sequenceId,
          sequenceComponentId,
          questionnaireId,
          score,
          doneAt: new Date().toISOString(),
          recurrenceType: null,
        },
      ];
    });
  };

  const updateSequenceComponentDone = (userSequenceId: number, sequenceComponentId: number) => {
    setSequences((prev) =>
      prev.map((seq) =>
        seq.id === userSequenceId
          ? {
              ...seq,
              components: seq.components.map((c) =>
                c.id === sequenceComponentId
                  ? { ...c, is_done: true, done_at: new Date().toISOString() }
                  : c
              ),
            }
          : seq
      )
    );
  };

  const updateSequenceComponentProgress = (
    userSequenceId: number,
    sequenceComponentId: number,
    answeredCount: number
  ) => {
    setSequences((prev) =>
      prev.map((seq) =>
        seq.id === userSequenceId
          ? {
              ...seq,
              components: seq.components.map((c) =>
                c.id === sequenceComponentId
                  ? { ...c, answered_questions_count: answeredCount }
                  : c
              ),
            }
          : seq
      )
    );
  };

  const updateFullSequenceAnswer = (
    userSequenceId: number,
    questionnaireId: number,
    question: QuestionType,
    value: AnswerValueType
  ) => {
    setFullSequenceByUserSequenceId((prev) => {
      const current = prev[userSequenceId];

      if (!current) {
        return prev;
      }

      const normalizedAnswerId =
        question.type === QuestionTypeEnum.CHECKBOX
          ? Array.isArray(value)
            ? value.map(Number)
            : []
          : question.type === QuestionTypeEnum.RADIOBUTTON ||
              question.type === QuestionTypeEnum.DROPDOWN
            ? value != null
              ? [Number(value)]
              : []
            : [];

      const normalizedValue = Array.isArray(value) ? value[0] ?? null : value;
      const nextAnswers = (current.answers?.answers ?? []).filter(
        (answer) => answer.question_id !== question.id
      );

      nextAnswers.push({
        questionnaire_id: questionnaireId,
        question_id: question.id,
        question_type_id: Number(question.detail.id ?? 0),
        answer_id: normalizedAnswerId,
        value: normalizedValue,
        is_empty_answer: value == null || (Array.isArray(value) && value.length === 0),
        answer_date: new Date().toISOString(),
      });

      return {
        ...prev,
        [userSequenceId]: {
          ...current,
          answers: {
            ...current.answers,
            answers: nextAnswers,
          },
        },
      };
    });
  };

  const updateSequenceDone = (userSequenceId: number) => {
    setSequences((prev) =>
      prev.map((seq) =>
        seq.id === userSequenceId ? { ...seq, status: SequenceStatusEnum.DONE } : seq
      )
    );
  };

  return (
    <ResonanceDataContext.Provider
      value={{
        sequences,
        scores,
        sequenceAvailabilityBySequenceId,
        hasFetchedSequences,
        loading,
        error,
        fetchSequences,
        fetchScores,
        fetchSequenceAvailability,
        ensureFreshSequences,
        fullSequenceByUserSequenceId,
        prefetchFullSequencesForStudySequenceIds,
        updateScore,
        updateSequenceComponentDone,
        updateSequenceComponentProgress,
        updateFullSequenceAnswer,
        updateSequenceDone,
      }}
    >
      {children}
    </ResonanceDataContext.Provider>
  );
};

export const useResonanceData = () => useContext(ResonanceDataContext);
